import { useEffect, useState } from 'react';
import { Sparkles, Save, Wand2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Button, Field, Spinner, LoadingState, ErrorState, inputClass } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import type { StyleProfile } from '@/lib/types';

const splitList = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

export function StyleProfilePage() {
  const { data, loading, error } = useApi<{ profile: StyleProfile | null }>('/style-profile');

  const [hasProfile, setHasProfile] = useState(false);
  const [summary, setSummary] = useState('');
  const [dominant, setDominant] = useState('');
  const [accent, setAccent] = useState('');
  const [materials, setMaterials] = useState('');
  const [formality, setFormality] = useState('');
  const [silhouettes, setSilhouettes] = useState('');
  const [brandAffinity, setBrandAffinity] = useState('');
  const [aesthetics, setAesthetics] = useState('');
  const [gaps, setGaps] = useState('');

  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const populate = (p: StyleProfile) => {
    setSummary(p.summary ?? '');
    setDominant((p.palette?.dominant ?? []).join(', '));
    setAccent((p.palette?.accent ?? []).join(', '));
    setMaterials((p.materials ?? []).join(', '));
    setFormality(p.formalityRange ?? '');
    setSilhouettes((p.silhouettes ?? []).join(', '));
    setBrandAffinity((p.brandAffinity ?? []).join(', '));
    setAesthetics((p.aesthetics ?? []).join(', '));
    setGaps((p.gaps ?? []).join(', '));
    setHasProfile(true);
  };

  useEffect(() => {
    if (data?.profile) populate(data.profile);
  }, [data]);

  const build = (): StyleProfile => ({
    palette: { dominant: splitList(dominant), accent: splitList(accent) },
    materials: splitList(materials),
    formalityRange: formality.trim(),
    silhouettes: splitList(silhouettes),
    brandAffinity: splitList(brandAffinity),
    aesthetics: splitList(aesthetics),
    gaps: splitList(gaps),
    summary: summary.trim(),
  });

  const generate = async () => {
    setGenerating(true);
    setGenError(null);
    setSaved(false);
    try {
      const res = await api.post<{ profile: StyleProfile }>('/ai/style-profile');
      if (res?.profile) populate(res.profile);
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Could not generate a profile.');
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/style-profile', { profile: build() });
      setSaved(true);
    } catch {
      /* ignore */
    } finally {
      setSaving(false);
    }
  };

  const listField = (label: string, value: string, set: (v: string) => void, placeholder: string) => (
    <Field label={label} hint="Comma-separated">
      <input type="text" value={value} onChange={(e) => { set(e.target.value); setSaved(false); }} placeholder={placeholder} className={inputClass} />
    </Field>
  );

  return (
    <Page>
      <PageHeader section="Style Profile" backHref="/profile" backLabel="Profile" />
      <Main className="space-y-5">
        <Card className="p-4 flex items-start gap-3 bg-primary/5 border-primary/20">
          <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            Your style profile captures your taste — palette, materials, brands, and aesthetic. The AI stylist uses it
            when building fits. Generate it from your closet, then edit anything that doesn't feel right.
          </p>
        </Card>

        {loading && <LoadingState label="Loading your profile…" />}
        {error && <ErrorState message={error} />}

        {!loading && (
          <>
            <Button fullWidth variant={hasProfile ? 'secondary' : 'primary'} onClick={generate} disabled={generating}>
              {generating ? <><Spinner className="w-4 h-4" /> Reading your closet…</> : <><Wand2 className="w-4 h-4" /> {hasProfile ? 'Regenerate from my closet' : 'Generate from my closet'}</>}
            </Button>
            {generating && (
              <p className="text-[11px] text-muted-foreground text-center -mt-2">This takes ~10–20 seconds.</p>
            )}
            {genError && <p className="text-sm text-destructive">{genError}</p>}

            {hasProfile && (
              <>
                {summary.trim() && (
                  <Card className="p-4">
                    <p className="text-sm text-foreground leading-relaxed italic">{summary}</p>
                  </Card>
                )}

                <div className="space-y-4">
                  <Field label="Summary">
                    <textarea value={summary} onChange={(e) => { setSummary(e.target.value); setSaved(false); }} rows={3} className={cn(inputClass, 'resize-none')} placeholder="A short paragraph describing your taste" />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    {listField('Dominant colors', dominant, setDominant, 'Navy, Charcoal, Olive')}
                    {listField('Accent colors', accent, setAccent, 'Burgundy, Tan')}
                  </div>
                  {listField('Preferred materials', materials, setMaterials, 'Wool, Cashmere, Linen')}
                  <Field label="Formality range">
                    <input type="text" value={formality} onChange={(e) => { setFormality(e.target.value); setSaved(false); }} placeholder="Smart casual to business formal" className={inputClass} />
                  </Field>
                  {listField('Favored silhouettes / fits', silhouettes, setSilhouettes, 'Tailored, Relaxed, Slim')}
                  {listField('Brand affinity', brandAffinity, setBrandAffinity, 'Loro Piana, John Lobb')}
                  {listField('Aesthetic descriptors', aesthetics, setAesthetics, 'Quiet luxury, Tailored, Coastal')}
                  {listField('Notable gaps', gaps, setGaps, 'Casual outerwear, Summer shoes')}
                </div>

                <Button fullWidth onClick={save} disabled={saving}>
                  {saving ? <><Spinner className="w-4 h-4" /> Saving…</> : <><Save className="w-4 h-4" /> {saved ? 'Saved ✓' : 'Save Style Profile'}</>}
                </Button>
              </>
            )}
          </>
        )}
      </Main>
    </Page>
  );
}
